import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
// src/pages/warehouse/UmfahrenPage.tsx
// Maske des Lager-Mitarbeiters (NICHT des Pickers — Spec §7): Verschieben/Auslagern/Auspacken.
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useWedgeScanner, useCameraScanner } from '@/hooks/useScanner'
import { classifyScanFrontend } from '@/lib/scan'
import { useScanInfo, sendMove, flushMoves, moveQueue, useResolveArtikel, MoveOfflineUnavailableError, type ScanInfo, type MoveResult, type MoveRejection } from '@/api/warehouse'
import { apiFetch, isTransportError } from '@/api/client'
import { newIdempotencyKey } from '@/lib/offline-queue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RejectionList } from './components/RejectionList'
import { isCompanyReviewPending, warehouseErrorMessage } from '@/lib/warehouse-company'

type Phase =
  | { step: 'warte_scan' }
  | { step: 'dialog'; paletteNo: string; info: ScanInfo }
  | { step: 'warte_zielplatz'; paletteNo: string }
  // C-I2: siehe EinlagernPage.tsx — kumulierte Bestaetigungs-Flags, damit ein Resend nach
  // der ZWEITEN Warnung beide Confirms mitschickt statt zwischen occupied/inventur zu pingpongen.
  | { step: 'belegt_warnung'; paletteNo: string; placeBarcode: string; message: string; confirmInventur?: boolean }
  | { step: 'inventur_warnung'; paletteNo: string; placeBarcode?: string; message: string; retry: 'pickup' | 'place'; confirmMisch?: boolean }

// RA3-BE-A (W3-BE) Web-Pendant: Antwortform von /position/assign/ + /position/fremd/ —
// beide koennen jetzt `blocked: true, code: 'occupied_warning'` liefern (Zielplatz bereits
// belegt, analog dem move/place-Muster).
interface PositionWriteResult {
  blocked?: boolean; code?: string; message?: string; occupant_count?: number
  duplicate?: boolean; position_id?: number; hinweis?: string
}

export default function UmfahrenPage() {
  const [phase, setPhase] = useState<Phase>({ step: 'warte_scan' })
  const [manual, setManual] = useState('')
  const [fremd, setFremd] = useState({ artikel: '', freitext: '', platz: '' })
  const [fremdBusy, setFremdBusy] = useState(false)
  // RA-WI3: nach einem unklaren Fehler (Netz/5xx — koennte am Server angekommen sein oder
  // nicht) bleibt der idempotency_key stabil, damit ein Retry serverseitig dedupliziert.
  // Genau DESHALB duerfen die Eingaben bis zur expliziten Klaerung NICHT mehr editierbar
  // sein: sonst aendert der Nutzer z.B. den Platz, klickt erneut, der Server dedupliziert
  // gegen den ALTEN Key (alte Zuordnung) und meldet faelschlich Erfolg fuer die NEUE Eingabe.
  const [fremdUnclearError, setFremdUnclearError] = useState<string | null>(null)
  const [fremdReviewMessage, setFremdReviewMessage] = useState<string | null>(null)
  const [pendingCount, setPendingCount] = useState(moveQueue.pending().length)
  const [rejectionTick, setRejectionTick] = useState(0)
  const scanInfo = useScanInfo()
  const resolveArtikel = useResolveArtikel()
  const cam = useCameraScanner((code) => void handleScan(code))
  // C-I3: Single-Flight-Guard fuer die Fremd-/Artikel-Zuordnung — synchroner Ref-Check
  // verhindert Doppel-Tap-Doppelbuchungen; der Idempotency-Key bleibt bis zum Erfolg
  // stabil, damit ein Retry (Netzfehler, erneuter Klick) serverseitig dedupliziert wird
  // (Backend akzeptiert optional idempotency_key auf /position/assign/ + /position/fremd/, S3).
  const fremdBusyRef = useRef(false)
  const fremdKeyRef = useRef<string | null>(null)
  // RA3-BE-A (W3-BE) Web-Pendant: Zielplatz von assign/fremd kann jetzt bereits belegt
  // sein — der Server blockt (occupied_warning) statt still zu ueberschreiben. Analog dem
  // bestehenden belegt_warnung-Muster in Einlagern/Umfahren braucht es eine explizite
  // Misch-Bestaetigung.
  const [fremdOccupiedWarning, setFremdOccupiedWarning] = useState<{ message: string; occupantCount: number } | null>(null)
  // RA3-WEB-A: pickup-cancel ist ambig bei Transportfehlern (Server hat evtl. bereits
  // committet, nur die Antwort ging verloren) — Retry MUSS denselben idempotency_key
  // wiederverwenden (siehe EinlagernPage.tsx fuer die volle Begruendung).
  // RA4-WEB-1: Key an paletteNo gebunden + geleert beim Verlassen des Cancel-/Ziel-Platz-
  // Scan-Flows ueber einen anderen Weg (siehe EinlagernPage.tsx, identisches Muster).
  const cancelKeyRef = useRef<{ key: string; paletteNo: string } | null>(null)
  // W5-WEB (RA5-WEB-1): Single-Flight fuer pickup-cancel — Details siehe EinlagernPage.
  // Synchroner Ref-Guard gegen Doppel-Submit; Match-Guard in onCancel verwirft verspaetete
  // Antworten, deren Key nicht mehr der aktive Kontext ist.
  const cancelBusyRef = useRef(false)
  const [cancelBusy, setCancelBusy] = useState(false)

  const refreshPending = () => setPendingCount(moveQueue.pending().length)
  // B2: harte 4xx-Rejects werden aus der Queue entfernt, aber muessen dem Mitarbeiter
  // gemeldet werden (Buchung ist NICHT im Backend gelandet, muss ggf. manuell nachgeholt werden).
  // C-I5: Details (Palette/Aktion/Code/Meldung) landen zusaetzlich im Dead-Letter (RejectionList).
  const handleFlushResult = (r: { sent: number; failed: number; rejected: number; rejections: MoveRejection[] }) => {
    refreshPending()
    if (r.rejected > 0) {
      toast.error(`${r.rejected} gepufferte Buchung(en) vom Server abgelehnt — Details unten`)
      setRejectionTick((t) => t + 1)
    }
  }

  useEffect(() => {
    const t = setInterval(() => { void flushMoves().then(handleFlushResult) }, 10_000)
    const onOnline = () => { void flushMoves().then(handleFlushResult) }
    window.addEventListener('online', onOnline)
    return () => { clearInterval(t); window.removeEventListener('online', onOnline) }
  }, [])

  // RA4-WEB-1 (siehe EinlagernPage.tsx): 'warte_scan' bedeutet "kein Palettenvorgang mehr
  // aktiv" — jeder Weg dorthin verlaesst den Cancel-/Ziel-Platz-Scan-Flow fuer die zuletzt
  // gehaltene Palette; ein dort noch stehender Cancel-Key darf danach nicht mehr fuer eine
  // kuenftige, andere Palette wiederverwendet werden.
  useEffect(() => {
    if (phase.step === 'warte_scan') cancelKeyRef.current = null
  }, [phase.step])

  const onResult = useCallback((r: MoveResult | { queued: true }, okMsg: string): boolean => {
    if ('queued' in r) { toast.warning('OFFLINE — gepuffert (NICHT GEBUCHT)'); refreshPending(); return true }
    if (r.duplicate) { toast.info('Bereits gebucht'); return true }
    if (r.blocked) { toast.error(r.message ?? 'Abgelehnt'); return false }
    toast.success(okMsg)
    return true
  }, [])

  const handleScan = useCallback(async (code: string) => {
    // W5-WEB: waehrend ein Cancel in-flight ist, keine konkurrierende Buchung starten.
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    const scan = classifyScanFrontend(code)
    if (phase.step === 'warte_zielplatz' || phase.step === 'belegt_warnung') {
      if (scan.kind !== 'place') { toast.error('Jetzt den ZIEL-Platz scannen'); return }
      const r = await sendMove('/warehouse/move/place/', {
        palette_no: phase.paletteNo, place_barcode: scan.barcode, confirm_misch: false,
        idempotency_key: newIdempotencyKey(),
      })
      if (!('queued' in r) && r.blocked && r.code === 'occupied_warning') {
        setPhase({ step: 'belegt_warnung', paletteNo: phase.paletteNo, placeBarcode: scan.barcode, message: r.message ?? 'Platz belegt' })
        return
      }
      if (!('queued' in r) && r.blocked && r.code === 'inventur_warnung') {
        setPhase({ step: 'inventur_warnung', paletteNo: phase.paletteNo, placeBarcode: scan.barcode, message: r.message ?? 'Bereich wird gerade gezählt — trotzdem buchen?', retry: 'place' })
        return
      }
      if (onResult(r, `Verschoben → ${scan.barcode} (First-In wandert mit)`)) setPhase({ step: 'warte_scan' })
      return
    }
    if (scan.kind !== 'palette') {
      toast.error(scan.kind === 'place' ? 'Zum Umfahren die PALETTE scannen (nicht den Platz)' : `Unbekannter Code: ${code}`)
      return
    }
    const info = await scanInfo.mutateAsync(code)   // K3: Info zuerst
    setPhase({ step: 'dialog', paletteNo: scan.paletteNo, info })
  }, [phase, scanInfo, onResult])

  useWedgeScanner((code) => void handleScan(code), true)

  async function onVerschieben() {
    if (phase.step !== 'dialog') return
    const r = await sendMove('/warehouse/move/pickup/', { palette_no: phase.paletteNo, idempotency_key: newIdempotencyKey() })
    if (!('queued' in r) && r.blocked && r.code === 'inventur_warnung') {
      setPhase({ step: 'inventur_warnung', paletteNo: phase.paletteNo, message: r.message ?? 'Bereich wird gerade gezählt — trotzdem buchen?', retry: 'pickup' })
      return
    }
    if (onResult(r, 'Aufgenommen — Ziel-Platz scannen')) setPhase({ step: 'warte_zielplatz', paletteNo: phase.paletteNo })
  }
  async function onAuslagern(target: 'produktion' | 'ausgang') {
    if (phase.step !== 'dialog') return
    const r = await sendMove('/warehouse/move/unload/', { palette_no: phase.paletteNo, target, idempotency_key: newIdempotencyKey() })
    if (onResult(r, `Ausgelagert (${target === 'produktion' ? 'Packstation' : 'Ausgang'}) — Platz ist frei`)) setPhase({ step: 'warte_scan' })
  }
  async function onAuspacken() {
    if (phase.step !== 'dialog') return
    const r = await sendMove('/warehouse/move/unpack/', { palette_no: phase.paletteNo, idempotency_key: newIdempotencyKey() })
    if (!('queued' in r) && !r.blocked) {
      toast.success(`Ausgepackt — ab jetzt ca. ${r.qty_circa ?? '?'} St.`)
      if (r.hint_mehrfach_offen) toast.warning('Hinweis: Mehr als eine Palette dieses Artikels ist offen (Eine-offene-Palette-Regel)')
      setPhase({ step: 'warte_scan' })
    } else { onResult(r, '') }
  }
  // Wiedereinstieg fuer eine bereits in_transit gescannte Palette (Reload-Fall, Review Task 16 Finding 1) —
  // genutzt vom Dialog-Resume-Block; onSkip zusaetzlich von warte_zielplatz (paletteNo statt phase-Read, damit beide Stellen teilen).
  async function onSkip(paletteNo: string) {
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    const reason = window.prompt('Grund (wird Klaerfall)?')
    if (!reason) return
    const r = await sendMove('/warehouse/move/skip/', { palette_no: paletteNo, reason, idempotency_key: newIdempotencyKey() })
    if (onResult(r, 'Uebersprungen — Klaerfall angelegt')) setPhase({ step: 'warte_scan' })
  }
  async function onCancel(paletteNo: string) {
    // RA-WC1: pickup-cancel ist bewusst online-only (allowOffline:false) — ein gepuffertes
    // 'queued' wuerde was_fresh_intake verlieren und der Nutzer laeuft danach zum Start,
    // waehrend die Palette serverseitig weiter in_transit haengt. Bei fehlender Verbindung
    // bleibt die Palette unveraendert im aktuellen Schritt stehen.
    // W5-WEB (RA5-WEB-1): Single-Flight — zweiter Klick waehrend in-flight wird verworfen.
    if (cancelBusyRef.current) return
    if (!cancelKeyRef.current || cancelKeyRef.current.paletteNo !== paletteNo) {
      cancelKeyRef.current = { key: newIdempotencyKey(), paletteNo }
    }
    const idempotencyKey = cancelKeyRef.current.key
    cancelBusyRef.current = true
    setCancelBusy(true)
    try {
      const r = await sendMove('/warehouse/move/pickup-cancel/', { palette_no: paletteNo, idempotency_key: idempotencyKey }, { allowOffline: false })
      // W5-WEB Match-Guard: verspaetete Antwort nur anwenden, wenn Key+Palette noch der
      // aktive Cancel-Kontext sind (sonst hat ein anderer Flow uebernommen — verwerfen).
      if (cancelKeyRef.current?.key !== idempotencyKey || cancelKeyRef.current.paletteNo !== paletteNo) return
      // Definitive Server-Antwort erhalten — der Vorgang ist abgeschlossen, ein etwaiger
      // naechster Cancel-Versuch ist eine NEUE Handlung und bekommt einen frischen Key.
      cancelKeyRef.current = null
      // C-C1 (Pendant B-C1): war_fresh_intake===false → Palette bleibt in_transit (nur der
      // Aufnehmer wurde freigegeben) — NICHT "abgebrochen" melden, im Ziel-Platz-Scan bleiben.
      if (!('queued' in r) && !r.blocked && r.was_fresh_intake === false) {
        toast.warning('Palette bleibt in Bewegung — Rückstellplatz scannen')
        setPhase({ step: 'warte_zielplatz', paletteNo })
        return
      }
      if (onResult(r, 'Aufnahme abgebrochen')) setPhase({ step: 'warte_scan' })
    } catch (e) {
      if (e instanceof MoveOfflineUnavailableError) {
        // RA3-WEB-A: Ausgang unklar — Key bleibt STEHEN, ein Retry muss ihn wiederverwenden.
        toast.error('Abbrechen braucht Verbindung — bitte online erneut versuchen')
        return
      }
      // Harter fachlicher Fehler (kein Transportfehler) — Server hat definitiv nicht committet.
      // Key nur loeschen, wenn er noch zu DIESEM Versuch gehoert.
      if (cancelKeyRef.current?.key === idempotencyKey) cancelKeyRef.current = null
      toast.error(e instanceof Error ? e.message : 'Abbrechen fehlgeschlagen')
    } finally {
      cancelBusyRef.current = false
      setCancelBusy(false)
    }
  }
  async function onConfirmMisch() {
    if (phase.step !== 'belegt_warnung') return
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    const r = await sendMove('/warehouse/move/place/', {
      palette_no: phase.paletteNo, place_barcode: phase.placeBarcode, confirm_misch: true,
      confirm_inventur: phase.confirmInventur === true, idempotency_key: newIdempotencyKey(),
    })
    // C-I2: Warn-Kumulierung — blockt der Server trotz confirm_misch:true jetzt mit
    // inventur_warnung, confirmMisch:true weitertragen fuer den naechsten Resend.
    if (!('queued' in r) && r.blocked && r.code === 'inventur_warnung') {
      setPhase({
        step: 'inventur_warnung', paletteNo: phase.paletteNo, placeBarcode: phase.placeBarcode,
        message: r.message ?? 'Bereich wird gerade gezählt — trotzdem buchen?', retry: 'place', confirmMisch: true,
      })
      return
    }
    if (onResult(r, 'Als MISCHPALETTE gebucht')) setPhase({ step: 'warte_scan' })
  }
  async function onConfirmInventur() {
    if (phase.step !== 'inventur_warnung') return
    if (cancelBusyRef.current) { toast.info('Abbruch läuft — bitte kurz warten'); return }
    if (phase.retry === 'pickup') {
      const r = await sendMove('/warehouse/move/pickup/', {
        palette_no: phase.paletteNo, idempotency_key: newIdempotencyKey(), confirm_inventur: true,
      })
      if (onResult(r, 'Aufgenommen — Ziel-Platz scannen')) setPhase({ step: 'warte_zielplatz', paletteNo: phase.paletteNo })
      return
    }
    const r = await sendMove('/warehouse/move/place/', {
      palette_no: phase.paletteNo, place_barcode: phase.placeBarcode, confirm_misch: phase.confirmMisch === true,
      idempotency_key: newIdempotencyKey(), confirm_inventur: true,
    })
    // C-I2: Warn-Kumulierung — blockt der Server trotz confirm_inventur:true jetzt mit
    // occupied_warning, confirmInventur:true weitertragen fuer den Misch-Resend.
    if (!('queued' in r) && r.blocked && r.code === 'occupied_warning') {
      setPhase({
        step: 'belegt_warnung', paletteNo: phase.paletteNo, placeBarcode: phase.placeBarcode!,
        message: r.message ?? 'Platz belegt', confirmInventur: true,
      })
      return
    }
    if (onResult(r, `Verschoben → ${phase.placeBarcode} (First-In wandert mit)`)) setPhase({ step: 'warte_scan' })
  }
  function onCancelInventurWarnung() {
    if (phase.step !== 'inventur_warnung') return
    // pickup-Warnung: noch nichts gebucht → zurueck zum Start. place-Warnung: Palette ist
    // bereits in_transit → zurueck zu warte_zielplatz (Muster wie belegt_warnung).
    setPhase(phase.retry === 'pickup' ? { step: 'warte_scan' } : { step: 'warte_zielplatz', paletteNo: phase.paletteNo })
  }
  // confirmMisch=true: Resend nach einer bereits gezeigten occupied_warning (RA3-BE-A/
  // W3-BE Web-Pendant) — derselbe Key wird wiederverwendet (der vorherige Versuch war
  // ein `blocked`-Response, also DEFINITIV kein Insert).
  async function onFremdBelegung(confirmMisch = false) {
    // C-I3: Single-Flight — synchroner Ref-Check VOR jedem await verhindert, dass ein
    // Doppel-Tap zwei Zuordnungen ausloest, bevor der State-Update (fremdBusy) gerendert ist.
    if (fremdBusyRef.current) return
    fremdBusyRef.current = true
    setFremdReviewMessage(null)
    setFremdBusy(true)
    // Stabiler Key pro Zuordnungs-Versuch — bleibt ueber Fehlschlag/Retry/occupied_warning
    // erhalten (damit der Server einen Netz-Retry bzw. den Misch-Resend dedupliziert),
    // wird erst NACH einer DEFINITIVEN Antwort (Erfolg oder klarer 4xx) zurueckgesetzt.
    if (!fremdKeyRef.current) fremdKeyRef.current = newIdempotencyKey()
    const idempotencyKey = fremdKeyRef.current
    try {
      if (fremd.artikel) {
        await resolveArtikel.mutateAsync(fremd.artikel) // wirft bei unbekannt
        const r = await apiFetch<PositionWriteResult>('/warehouse/position/assign/', {
          method: 'POST',
          body: JSON.stringify({ artikel: fremd.artikel, place_barcode: fremd.platz, idempotency_key: idempotencyKey, confirm_misch: confirmMisch }),
        })
        if (r.blocked && r.code === 'occupied_warning') {
          setFremdOccupiedWarning({ message: r.message ?? 'Platz belegt', occupantCount: r.occupant_count ?? 0 })
          return
        }
        toast.success(`${fremd.artikel} liegt jetzt auf ${fremd.platz} (ohne Menge — V1)`)
      } else {
        const r = await apiFetch<PositionWriteResult>('/warehouse/position/fremd/', {
          method: 'POST',
          body: JSON.stringify({ raw_code: '', free_text: fremd.freitext, place_barcode: fremd.platz, idempotency_key: idempotencyKey, confirm_misch: confirmMisch }),
        })
        if (r.blocked && r.code === 'occupied_warning') {
          setFremdOccupiedWarning({ message: r.message ?? 'Platz belegt', occupantCount: r.occupant_count ?? 0 })
          return
        }
        toast.warning('Gebucht OHNE Plattform-Artikel — landet in der Pflege-Liste "Nicht verknuepft"')
      }
      fremdKeyRef.current = null
      setFremdUnclearError(null)
      setFremdOccupiedWarning(null)
      setFremd({ artikel: '', freitext: '', platz: '' })
    } catch (e) {
      // RA3-WEB-B: nur Transport/5xx/Netz ist ambig (Server hat evtl. schon committet) —
      // Key bleibt stehen, Eingabe wird bis zur expliziten Klaerung gesperrt. Ein klarer
      // fachlicher 4xx (z.B. "Artikel unbekannt", Validierung) ist eine DEFINITIVE Antwort:
      // nichts wurde gebucht, kein Lock noetig — Nutzer korrigiert die Eingabe und ein
      // erneuter Versuch bekommt bewusst einen frischen Key (nichts zu deduplizieren).
      const msg = e instanceof Error ? e.message : 'Fehlgeschlagen'
      if (isCompanyReviewPending(e)) {
        setFremdReviewMessage(warehouseErrorMessage(e))
        // Typed denial happens before write. Keep the same key for a later explicit attempt.
      } else if (isTransportError(e)) {
        setFremdUnclearError(msg)
        toast.error(`${msg} — Eingabe gesperrt, bis geklärt ist ob die Buchung angekommen ist`)
      } else {
        fremdKeyRef.current = null
        toast.error(msg)
      }
    } finally {
      fremdBusyRef.current = false
      setFremdBusy(false)
    }
  }
  function onDiscardFremd() {
    fremdKeyRef.current = null
    setFremdUnclearError(null)
    setFremd({ artikel: '', freitext: '', platz: '' })
  }
  function onCancelFremdOccupied() {
    // Nutzer waehlt bewusst einen anderen Platz statt der Mischbestaetigung — das ist eine
    // NEUE Handlung, kein Retry derselben Op, deshalb Key verwerfen (naechster Versuch
    // bekommt einen frischen). Eingaben bleiben erhalten (nur der Platz muss meist geaendert
    // werden), Formular wird NICHT zurueckgesetzt — Muster wie belegt_warnung in Einlagern/Umfahren.
    fremdKeyRef.current = null
    setFremdOccupiedWarning(null)
  }

  const dialog = phase.step === 'dialog' ? phase.info.dialog : null

  return (
    <div className="p-4 max-w-xl mx-auto space-y-4">
      <h1 className="text-xl font-bold">Ware umfahren</h1>

      {pendingCount > 0 && (
        <div className="rounded-lg bg-red-600 text-white p-3 font-bold text-center">
          {pendingCount} Buchung(en) NICHT GEBUCHT — werden automatisch nachgesendet
        </div>
      )}
      <RejectionList refreshKey={rejectionTick} />

      {phase.step === 'warte_scan' && (
        <div className="rounded-xl border-2 border-dashed border-line p-8 text-center space-y-3">
          <div className="text-2xl font-bold">Paletten-Zettel scannen</div>
          {cam.supported
            ? <Button variant="outline" onClick={() => (cam.active ? cam.stop() : void cam.start())}>{cam.active ? 'Kamera stoppen' : 'Kamera-Scan'}</Button>
            : <div className="text-xs text-muted">Kamera-Scan nicht verfuegbar — Scanner oder manuelle Eingabe</div>}
          <video ref={cam.videoRef} className={cam.active ? 'w-full rounded-lg' : 'hidden'} muted playsInline />
          <Input className="w-56 mx-auto" placeholder="Code eintippen…" value={manual} onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && manual) { void handleScan(manual); setManual('') } }} />
        </div>
      )}

      {phase.step === 'dialog' && (
        <div className={`rounded-xl border p-5 space-y-3 ${dialog?.alreadyUnpacked ? 'border-4 border-red-600 bg-red-50 dark:bg-red-950' : 'border-line bg-panel'}`}>
          {dialog?.alreadyUnpacked && <div className="text-xl font-bold text-red-700 text-center">PALETTE BEREITS AUSGEPACKT</div>}
          <div className="text-lg font-bold">{phase.paletteNo} — <GrundartikelLink code={phase.info.artikel?.merchantSku} /></div>
          <div className="text-muted">{phase.info.artikel?.itemName}</div>
          <div>
            Zustand: <b>{phase.info.position?.state ?? 'unbekannt'}</b>
            {phase.info.place && <> auf <b>{(phase.info.place as { barcode?: string }).barcode}</b></>}
            {phase.info.position?.state === 'verschlossen' && <> · {phase.info.position.qtyExact} St. (exakt)</>}
            {phase.info.position?.state === 'ausgepackt' && <> · ca. {phase.info.position.qtyCirca ?? '?'} St.</>}
            {phase.info.position?.isMisch && <span className="ml-1 text-xs bg-amber-200 text-amber-900 px-1 rounded">MISCH</span>}
          </div>
          {phase.info.position?.state === 'in_transit' ? (
            // Finding 1: dialogOptionsFor() liefert fuer 'in_transit' den 3-Optionen-Catch-all (nur 'ausgepackt'
            // ist gesondert) — die drei Server-Optionen greifen hier ins Leere (verschieben/auspacken werden vom
            // Backend als already_in_transit/invalid_state geblockt) oder wirken widerspruechlich. Deshalb bewusst
            // NICHT die Server-Optionen rendern, sondern den Wiedereinstieg (K2-Lockout-Fix, Review Task 16).
            <div className="space-y-2">
              <div className="rounded bg-amber-100 text-amber-900 px-2 py-1 text-sm font-semibold">
                Diese Palette ist bereits in Bewegung — weitermachen oder aufloesen:
              </div>
              <Button className="w-full text-lg py-6" onClick={() => setPhase({ step: 'warte_zielplatz', paletteNo: phase.paletteNo })}>
                Weiter: Ziel-Platz jetzt scannen
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" disabled={cancelBusy} onClick={() => void onCancel(phase.paletteNo)}>{cancelBusy ? 'Abbruch läuft…' : 'Aufnahme abbrechen'}</Button>
                <Button variant="ghost" className="flex-1" onClick={() => void onSkip(phase.paletteNo)}>Ueberspringen (Klaerfall)</Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-2 pt-1">
              {dialog?.options.includes('verschieben') && <Button className="py-6" onClick={() => void onVerschieben()}>Palette verschieben (danach Ziel-Platz scannen)</Button>}
              {dialog?.options.includes('auslagern') && (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 py-6" onClick={() => void onAuslagern('produktion')}>Auslagern → Packstation</Button>
                  <Button variant="outline" className="flex-1 py-6" onClick={() => void onAuslagern('ausgang')}>Auslagern → Ausgang</Button>
                </div>
              )}
              {dialog?.options.includes('auspacken') && <Button variant="outline" className="py-6" onClick={() => void onAuspacken()}>Palette auspacken (Menge wird „ca.")</Button>}
            </div>
          )}
          <Button variant="ghost" onClick={() => setPhase({ step: 'warte_scan' })}>Zurueck</Button>
        </div>
      )}

      {phase.step === 'warte_zielplatz' && (
        <div className="rounded-xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-950 p-5 text-center space-y-2">
          <div className="text-lg font-bold">Palette {phase.paletteNo} in Bewegung</div>
          <div className="text-2xl font-bold">ZIEL-Platz scannen (Pflicht)</div>
          {/* Manuelle Eingabe auch hier (Browser-E2E-Befund 2026-07-09): ohne Scanner war
              dieser Schritt eine Sackgasse. */}
          <Input className="w-56 mx-auto" placeholder="Platz-Code eintippen…" value={manual} onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && manual) { void handleScan(manual); setManual('') } }} />
          <Button variant="outline" onClick={() => void onSkip(phase.paletteNo)}>Ueberspringen (Klaerfall)</Button>
        </div>
      )}

      {phase.step === 'belegt_warnung' && (
        <div className="rounded-xl border-4 border-red-600 bg-red-50 dark:bg-red-950 p-5 space-y-4 text-center">
          <div className="text-2xl font-bold text-red-700">⚠ PLATZ BELEGT</div>
          <div>{phase.message}</div>
          <Button className="w-full text-lg py-6" onClick={() => setPhase({ step: 'warte_zielplatz', paletteNo: phase.paletteNo })}>
            ABBRECHEN — richtigen Platz scannen
          </Button>
          <button className="text-xs underline text-muted" onClick={() => void onConfirmMisch()}>
            Bewusst als Mischpalette auf {phase.placeBarcode} buchen
          </button>
        </div>
      )}

      {phase.step === 'inventur_warnung' && (
        <div className="rounded-xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-950 p-5 space-y-4 text-center">
          <div className="text-xl font-bold text-amber-800">⚠ INVENTUR LÄUFT</div>
          <div>{phase.message}</div>
          <Button className="w-full text-lg py-6" onClick={() => void onConfirmInventur()}>
            Trotzdem buchen
          </Button>
          <button className="text-xs underline text-muted" onClick={onCancelInventurWarnung}>
            Abbrechen
          </button>
        </div>
      )}

      <div className="rounded-xl border border-line bg-panel p-4 space-y-2">
        <div className="font-semibold text-sm">Freie Belegung (Fachplatz „liegt dort" / Fremdware — F1b)</div>
        {fremdReviewMessage && <p role="alert" className="rounded-md border border-amber-500/50 bg-amber-500/10 p-2 text-sm">{fremdReviewMessage}</p>}
        {fremdUnclearError && (
          <div className="rounded-md border border-red-400 bg-red-50 dark:bg-red-950 p-2 text-sm text-red-700">
            Unklarer Fehler: {fremdUnclearError} — Eingabe gesperrt, bis geklärt ist ob die Buchung angekommen ist.
          </div>
        )}
        {fremdOccupiedWarning && (
          <div className="rounded-md border-2 border-red-600 bg-red-50 dark:bg-red-950 p-3 text-sm space-y-2">
            <div className="font-bold text-red-700">⚠ PLATZ BELEGT</div>
            <div>{fremdOccupiedWarning.message}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={fremdBusy} onClick={() => void onFremdBelegung(true)}>
                {fremdBusy ? 'Sende…' : 'Bewusst als Mischbelegung buchen'}
              </Button>
              <Button variant="ghost" size="sm" disabled={fremdBusy} onClick={onCancelFremdOccupied}>Abbrechen — anderen Platz wählen</Button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Input className="w-40" disabled={fremdUnclearError !== null || fremdOccupiedWarning !== null} placeholder="Artikel (Sternnr.) ODER leer" value={fremd.artikel} onChange={(e) => setFremd({ ...fremd, artikel: e.target.value })} />
          <Input className="w-48" disabled={fremdUnclearError !== null || fremdOccupiedWarning !== null} placeholder="Freitext (wenn kein Artikel)" value={fremd.freitext} onChange={(e) => setFremd({ ...fremd, freitext: e.target.value })} />
          <Input className="w-40" disabled={fremdUnclearError !== null || fremdOccupiedWarning !== null} placeholder="Platz (R26+F09+P1+EB1)" value={fremd.platz} onChange={(e) => setFremd({ ...fremd, platz: e.target.value })} />
          {fremdUnclearError ? (
            <>
              <Button variant="outline" disabled={fremdBusy} onClick={() => void onFremdBelegung()}>
                {fremdBusy ? 'Sende…' : 'Erneut senden (gleiche Daten)'}
              </Button>
              <Button variant="ghost" disabled={fremdBusy} onClick={onDiscardFremd}>Verwerfen</Button>
            </>
          ) : fremdOccupiedWarning ? null : (
            <Button variant="outline" disabled={fremdBusy || !fremd.platz || (!fremd.artikel && fremd.freitext.length < 2)} onClick={() => void onFremdBelegung()}>
              {fremdBusy ? 'Ordne zu…' : 'Zuordnen'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
